// Shared i18n scaffold for customer-app + admin-app.
//
// Strategy:
//   - Single i18next instance, react-i18next bindings.
//   - Two namespaces baked in: en + es. Resource bundles are JSON files
//     co-located with this module (`en.json`, `es.json`).
//   - Apps call `initI18n()` once during boot (before <App /> mounts) so
//     the global singleton is ready before the first `useTranslation()`
//     call. Calling it twice is a no-op (i18next checks `isInitialized`).
//   - Language preference persists in localStorage under `STORAGE_KEY`.
//     Server-side persistence (TenantUser.languagePreference) is a
//     stretch goal — schema column does not exist yet, so this scaffold
//     stays client-only by design.

import i18n, { type i18n as I18nInstance } from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './en.json';
import es from './es.json';

export const LANGUAGES = ['en', 'es'] as const;
export type Language = (typeof LANGUAGES)[number];

export const DEFAULT_LANGUAGE: Language = 'en';
export const STORAGE_KEY = 'ferro.lang';

export const RESOURCES = { en: { translation: en }, es: { translation: es } } as const;

// Read the saved preference from localStorage with a defensive guard for
// SSR / Node test environments where `window` is undefined. Falls back
// to the default language on parse failure or unknown values.
export function readSavedLanguage(): Language {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return DEFAULT_LANGUAGE;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && (LANGUAGES as readonly string[]).includes(raw)) {
      return raw as Language;
    }
  } catch {
    // localStorage access can throw in private-browsing / sandboxed
    // contexts. Swallow and fall through.
  }
  return DEFAULT_LANGUAGE;
}

// Persist the language preference. Safe in non-browser environments —
// silently no-ops if localStorage is unavailable.
export function saveLanguage(lang: Language): void {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // private-browsing quotas etc. — same defensive swallow.
  }
}

// Initialize the shared i18next singleton. Idempotent; safe to call
// from multiple entry points (customer-app + admin-app both invoke
// from their `main.tsx`). The `initialLng` parameter lets callers
// override the saved-language lookup (useful for tests).
export function initI18n(initialLng?: Language): I18nInstance {
  if (i18n.isInitialized) {
    return i18n;
  }

  const lng = initialLng ?? readSavedLanguage();

  void i18n
    .use(initReactI18next)
    .init({
      resources: RESOURCES,
      lng,
      fallbackLng: DEFAULT_LANGUAGE,
      supportedLngs: LANGUAGES as readonly string[],
      defaultNS: 'translation',
      interpolation: {
        escapeValue: false, // React already escapes by default
      },
      react: {
        // We rely on Suspense via lazy load in apps, but the bundles are
        // baked in synchronously so we don't actually suspend. Disabling
        // explicitly keeps the boot sync and avoids surprise fallbacks.
        useSuspense: false,
      },
      returnNull: false,
    });

  return i18n;
}

// Change the active language and persist. Both customer-app + admin-app
// use this from their LanguageToggle button.
export async function changeLanguage(lang: Language): Promise<void> {
  saveLanguage(lang);
  if (i18n.isInitialized && i18n.language !== lang) {
    await i18n.changeLanguage(lang);
  }
}

export { i18n };
